import { useEffect, useRef } from "react";

// Edge-fade affordance for horizontal scroll strips (Round 10.2). Strips
// that scroll within themselves hide their scrollbars, so on touch
// devices clipped content is indistinguishable from scrollable content.
// This hook toggles fade-left / fade-right classes on the scroll
// container from its scroll position; styles.css masks the faded edge so
// cut-off content visibly continues. Pure class toggling on the DOM node
// (no state) — scroll events never re-render the component.

export function computeEdgeFade(
  scrollLeft: number,
  clientWidth: number,
  scrollWidth: number,
): { left: boolean; right: boolean } {
  return {
    left: scrollLeft > 1,
    right: scrollLeft + clientWidth < scrollWidth - 1,
  };
}

// `contentKey` re-evaluates the fade when the strip's content changes
// without a box resize (e.g. the chip row expanding behind "+N more") —
// ResizeObserver only fires when the container's own box changes.
export function useEdgeFade<T extends HTMLElement>(contentKey?: unknown) {
  const ref = useRef<T | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => {
      const { left, right } = computeEdgeFade(
        el.scrollLeft,
        el.clientWidth,
        el.scrollWidth,
      );
      el.classList.toggle("fade-left", left);
      el.classList.toggle("fade-right", right);
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", update);
      ro.disconnect();
    };
  }, [contentKey]);
  return ref;
}
